/**
 * Breakpoint Context - A365 Core
 * 
 * Contexto React para manejo reactivo de breakpoints de Tailwind CSS.
 * Proporciona un hook useBreakpoint que actualiza automáticamente
 * cuando cambia el tamaño de la ventana.
 * 
 * Reemplaza el uso de Material-UI useTheme/useMediaQuery y react-responsive.
 * 
 * @author A365 Core Team
 * @version 1.0.0
 */

import React, { createContext, useContext, useEffect, useState } from 'react';
import { 
  getCurrentBreakpoint, 
  isBreakpointDown, 
  isBreakpointUp, 
  isMobile, 
  isTablet, 
  isDesktop,
  TAILWIND_BREAKPOINTS,
  BREAKPOINT_ORDER
} from '../services/breakpointService';

// Contexto
const BreakpointContext = createContext();

/**
 * Hook para usar el contexto de breakpoints
 * @returns {Object} Objeto con propiedades y métodos de breakpoint
 */
export const useBreakpointContext = () => {
  const context = useContext(BreakpointContext);
  if (!context) {
    throw new Error('useBreakpointContext debe ser usado dentro de un BreakpointProvider');
  }
  return context;
};

/**
 * Provider del contexto de breakpoints
 * @param {Object} props - Props del componente
 * @param {React.ReactNode} props.children - Componentes hijos
 * @param {number} props.debounceTime - Tiempo de debounce para resize (ms)
 */
export const BreakpointProvider = ({ children, debounceTime = 100 }) => {
  const [currentBreakpoint, setCurrentBreakpoint] = useState(() => getCurrentBreakpoint());
  const [windowWidth, setWindowWidth] = useState(() => 
    typeof window !== 'undefined' ? window.innerWidth : 1024
  );

  useEffect(() => {
    let timeoutId;

    const handleResize = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        const newWidth = window.innerWidth;
        const newBreakpoint = getCurrentBreakpoint(newWidth);
        
        setWindowWidth(newWidth);
        if (newBreakpoint !== currentBreakpoint) {
          setCurrentBreakpoint(newBreakpoint);
        }
      }, debounceTime);
    };

    // Solo agregar listener si estamos en el navegador
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', handleResize);
      
      // Verificar breakpoint inicial
      handleResize();
      
      return () => {
        window.removeEventListener('resize', handleResize);
        clearTimeout(timeoutId);
      };
    }
  }, [currentBreakpoint, debounceTime]);

  // Funciones helpers que usan el breakpoint actual
  const breakpointHelpers = {
    // Breakpoint actual
    current: currentBreakpoint,
    
    // Ancho de ventana actual
    width: windowWidth,
    
    // Verificaciones de breakpoint
    isXs: currentBreakpoint === 'xs',
    isSm: currentBreakpoint === 'sm',
    isMd: currentBreakpoint === 'md',
    isLg: currentBreakpoint === 'lg',
    isXl: currentBreakpoint === 'xl',
    is2Xl: currentBreakpoint === '2xl',
    
    // Helpers de comparación
    isDown: (target) => isBreakpointDown(target, currentBreakpoint),
    isUp: (target) => isBreakpointUp(target, currentBreakpoint),
    
    // Helpers de dispositivo
    isMobile: isMobile(currentBreakpoint),
    isTablet: isTablet(currentBreakpoint),
    isDesktop: isDesktop(currentBreakpoint),
    
    // Compatibilidad con Material-UI patterns
    // Equivalente a theme.breakpoints.down('xl')
    isMobileCompat: isBreakpointDown('xl', currentBreakpoint),
    // Equivalente a theme.breakpoints.down('md') 
    isMdDownCompat: isBreakpointDown('md', currentBreakpoint),
    
    // Acceso a configuración
    breakpoints: TAILWIND_BREAKPOINTS,
    order: BREAKPOINT_ORDER
  };

  return (
    <BreakpointContext.Provider value={breakpointHelpers}>
      {children}
    </BreakpointContext.Provider>
  );
};

// Exportar el contexto para casos avanzados
export { BreakpointContext };
